<template>
    <div>
        <template v-if="perPlatform">
            <div v-for="key in platformKeys" :key="key" class="rr-trend-block">
                <div class="rr-trend-title">
                    <span v-if="platformLogo(key)" class="rr-platform-logo" v-html="platformLogo(key)"></span>
                    {{ platformLabel(key) }} – Visibility Over Time
                </div>
                <brand-line-chart
                    v-if="brands.length"
                    ref="charts"
                    :selectedDates="filters.selectedDates"
                    :availableDates="datesByPlatform[key] || filters.selectedDates"
                    :dateRangeTouched="(filters.selectedDates || []).length >= 2"
                    :selectedBrands="chartBrands"
                    mode="Visibility"
                    :platform="key"
                    :tags="filters.tags"
                    :tagsOperator="filters.tagsOperator"
                    :maxBrands="maxBrands"
                    :ownBrand="ownBrandName"
                    :companyId="companyId"
                    :projectId="projectId"
                    @legend="(l) => setLegend(key, l)" />
                <div v-if="(legends[key] || []).length" class="rr-legend">
                    <span v-for="item in cappedLegend(key)" :key="item.label" class="rr-legend-item">
                        <span class="rr-legend-swatch" :style="{ background: item.color }"></span>{{ item.label }}
                    </span>
                    <span v-if="overflowCount(key)" class="rr-legend-more">+{{ overflowCount(key) }} more</span>
                </div>
            </div>
        </template>
        <template v-else>
            <brand-line-chart
                v-if="brands.length"
                ref="charts"
                :selectedDates="filters.selectedDates"
                :availableDates="datesAll"
                :dateRangeTouched="(filters.selectedDates || []).length >= 2"
                :selectedBrands="chartBrands"
                mode="Visibility"
                :platform="filters.platform && filters.platform !== 'all' ? filters.platform : null"
                :tags="filters.tags"
                :tagsOperator="filters.tagsOperator"
                :maxBrands="maxBrands"
                :ownBrand="ownBrandName"
                :companyId="companyId"
                :projectId="projectId"
                @legend="(l) => setLegend('all', l)" />
            <div v-if="(legends.all || []).length" class="rr-legend">
                <span v-for="item in cappedLegend('all')" :key="item.label" class="rr-legend-item">
                    <span class="rr-legend-swatch" :style="{ background: item.color }"></span>{{ item.label }}
                </span>
                <span v-if="overflowCount('all')" class="rr-legend-more">+{{ overflowCount('all') }} more</span>
            </div>
        </template>
        <div v-if="!brands.length" class="rr-empty">No brands tracked for this project.</div>
    </div>
</template>

<script>
    import BrandLineChart from "../../aisuite/BrandLineChart.vue"
    import { getPlatformMeta } from "../../../utils/platformMeta"
    import { availableDatesInRange, trendBrandScope } from "../reportApi"

    export default {
        name: "TrendChartsSection",
        components: { BrandLineChart },
        props: {
            filters: { type: Object, required: true },
            options: { type: Object, default: () => ({}) },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] },
            platformKeys: { type: Array, default: () => [] }
        },
        data() {
            return { legends: {}, legendCap: 16 }
        },
        computed: {
            perPlatform() {
                return !!this.options.per_platform && this.platformKeys.length > 0
            },
            ownBrandName() {
                return this.project?.brand?.brand_name || ""
            },
            // cached for a stable reference — the chart deep-watches selectedBrands
            brandScope() {
                return trendBrandScope(this.options, this.brands, this.project?.ai_brand_favorites, this.ownBrandName)
            },
            chartBrands() {
                return this.brandScope.brands
            },
            maxBrands() {
                return this.brandScope.maxBrands
            },
            // Cached so the array reference stays STABLE across re-renders. The chart deep-watches
            // availableDates; a fresh array on every render (e.g. when the legend updates) would
            // retrigger a fetch endlessly. Full list of processed days in range → all chart points.
            datesAll() {
                const dates = availableDatesInRange(this.project, this.filters.selectedDates, this.platformKeys)
                return dates.length ? dates : this.filters.selectedDates
            },
            datesByPlatform() {
                const map = {}
                this.platformKeys.forEach((key) => {
                    const dates = availableDatesInRange(this.project, this.filters.selectedDates, [key])
                    map[key] = dates.length ? dates : this.filters.selectedDates
                })
                return map
            }
        },
        methods: {
            setLegend(key, items) {
                this.$set(this.legends, key, Array.isArray(items) ? items : [])
            },
            // brands are sorted by visibility; show the most relevant, summarize the rest
            cappedLegend(key) {
                return (this.legends[key] || []).slice(0, this.legendCap)
            },
            overflowCount(key) {
                return Math.max(0, (this.legends[key] || []).length - this.legendCap)
            },
            platformLabel(key) {
                return getPlatformMeta(key).label || key
            },
            platformLogo(key) {
                return getPlatformMeta(key).logo || null
            },
            watchReady() {
                if (!this.brands.length) {
                    this.$emit("ready")
                    return
                }
                clearInterval(this._poll)
                this._poll = setInterval(() => {
                    const refs = this.$refs.charts
                    const charts = Array.isArray(refs) ? refs : refs ? [refs] : []
                    if (charts.length && charts.every((c) => !c.chartLoading)) {
                        clearInterval(this._poll)
                        // data is loaded but Chart.js still animates onto the canvas —
                        // emitting ready too early yields blank charts in the PDF capture
                        setTimeout(() => this.$emit("ready"), 1200)
                    }
                }, 250)
            }
        },
        mounted() {
            this.watchReady()
        },
        beforeDestroy() {
            clearInterval(this._poll)
        }
    }
</script>

<style scoped>
    .rr-trend-block {
        margin-bottom: 22px;
    }
</style>
